import { useTranslation } from 'i18n';
import { Box, Drawer, IconButton, Stack, styled, useBreakpoint, useLayoutContext } from 'ui';
import { ChevronLeft } from 'ui/icons';

import { HEADER_HEIGHT } from '../Header/Header';
import { SidebarProvider } from './SidebarContext';

export const NAV_SIDEBAR_WIDTH = 250;
export const isRouteActive = (currentPath: string, href: string) => {
  return currentPath.startsWith(href);
};

export const Sidebar = ({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation('features');
  const { isMobile } = useBreakpoint();

  return (
    <SidebarProvider value={{ onClose }}>
      <Drawer
        padding="small"
        sx={[
          (theme) => ({
            [theme.breakpoints.up('md')]: {
              flexShrink: 0,
              width: NAV_SIDEBAR_WIDTH,
            },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              pt: theme.spacing(HEADER_HEIGHT),
              width: NAV_SIDEBAR_WIDTH,
            },
          }),
        ]}
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={isOpen}
        onClose={onClose}
      >
        <Stack
          direction="column"
          sx={{
            height: '100%',
          }}
        >
          {children}

          <Box sx={{ justifySelf: 'center' }}>
            <div>
              <IconButton onClick={onClose} aria-label={t('navigation.sidebar.closeAriaLabel')}>
                <ChevronLeft />
              </IconButton>
            </div>
          </Box>
        </Stack>
      </Drawer>
    </SidebarProvider>
  );
};

const Root = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme }) => ({
  flexGrow: 1,
  marginLeft: `-${NAV_SIDEBAR_WIDTH}px`,
  minWidth: 0,
  paddingTop: theme.spacing(HEADER_HEIGHT),
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
  },
  transition: theme.transitions.create('margin', {
    duration: theme.transitions.duration.leavingScreen,
    easing: theme.transitions.easing.sharp,
  }),
  variants: [
    {
      props: ({ open }: { open?: boolean }) => open === true,
      style: {
        marginLeft: 0,
        transition: theme.transitions.create('margin', {
          duration: theme.transitions.duration.enteringScreen,
          easing: theme.transitions.easing.easeOut,
        }),
      },
    },
  ],
}));

export const Main = ({ isOpen = false, children }: { isOpen?: boolean; children: React.ReactNode }) => {
  const { isMobile } = useBreakpoint();
  const layoutContext = useLayoutContext();
  const showMainMove = layoutContext?.isDesktopSidebarOpen ?? (!isMobile && isOpen);

  return <Root open={showMainMove}>{children}</Root>;
};
